import { useMemo, useState } from 'react';
import {
  Stack, Title, Card, Group, Button, Table, Text, Skeleton, ActionIcon, Badge, Modal, TextInput, MultiSelect, Switch,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconTrash, IconSearch } from '@tabler/icons-react';
import {
  useAdminUsers, useAuthorities, useCreateUser, useUpdateUser, useDeleteUser,
  type AdminUser,
} from '../api/hooks/useUsers.ts';
import { HttpError } from '../api/client.ts';

/**
 * Users admin page (ADMIN-only). Lists every account in `jhi_user` and
 * lets an admin assign the system authorities ("ROLE_ADMIN", "ROLE_USER",
 * "ROLE_GOVERNMENT_FORM", …) that gate access to the various screens.
 * Authority assignment goes through the role-based system in /api/users
 * which replaces the `jhi_user_authority` join rows on each PUT.
 */
export function UsersPage() {
  const users = useAdminUsers();
  const authorities = useAuthorities();
  const create = useCreateUser();
  const update = useUpdateUser();
  const remove = useDeleteUser();
  const [editing, setEditing] = useState<AdminUser | 'new' | null>(null);
  const [filter, setFilter] = useState('');

  const rows = useMemo(() => {
    const all = users.data ?? [];
    const f = filter.trim().toLowerCase();
    if (!f) return all;
    return all.filter(u =>
      [u.login, u.email, u.firstName, u.lastName]
        .filter(Boolean)
        .some(s => String(s).toLowerCase().includes(f)),
    );
  }, [users.data, filter]);

  function onDelete(u: AdminUser) {
    modals.openConfirmModal({
      title: 'Delete user',
      children: <Text size="sm">Permanently deletes <strong>{u.login}</strong> and all their tokens. Continue?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        remove.mutate(u.login, {
          onSuccess: () => notifications.show({ message: 'Deleted', color: 'green' }),
          onError: err => notifications.show({ message: errorMessage(err), color: 'red' }),
        });
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Title order={2}>Users</Title>
        <Group>
          <TextInput
            leftSection={<IconSearch size={16} />}
            placeholder="Search users"
            value={filter}
            onChange={e => setFilter(e.currentTarget.value)}
            w={240}
          />
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>
            New user
          </Button>
        </Group>
      </Group>

      <Card withBorder padding={0} radius="md">
        {users.isPending ? (
          <Stack gap="xs" p="md">{[1,2,3,4,5].map(i => <Skeleton key={i} h={28} />)}</Stack>
        ) : users.isError ? (
          <Text p="md" c="red">{errorMessage(users.error)}</Text>
        ) : (
          <Table.ScrollContainer minWidth={700}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Login</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Activated</Table.Th>
                  <Table.Th>Authorities</Table.Th>
                  <Table.Th style={{ width: 80 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="md">No users match.</Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {rows.map(u => (
                  <Table.Tr key={u.id}>
                    <Table.Td>{u.login}</Table.Td>
                    <Table.Td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</Table.Td>
                    <Table.Td>{u.email}</Table.Td>
                    <Table.Td>
                      {u.activated
                        ? <Badge color="green" variant="light">Yes</Badge>
                        : <Badge color="gray" variant="light">No</Badge>}
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="wrap">
                        {(u.authorities ?? []).map(a => (
                          <Badge
                            key={a}
                            size="sm"
                            color={a === 'ROLE_ADMIN' ? 'red' : 'blue'}
                            variant="light"
                          >
                            {a}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end">
                        <ActionIcon variant="subtle" onClick={() => setEditing(u)} aria-label="Edit">
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" color="red" onClick={() => onDelete(u)} aria-label="Delete">
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {editing !== null && (
        <UserEditModal
          initial={editing === 'new'
            ? {
                login: '', firstName: '', lastName: '', email: '',
                activated: true, langKey: 'en', authorities: ['ROLE_USER'],
              }
            : editing}
          isNew={editing === 'new'}
          allAuthorities={authorities.data ?? []}
          onClose={() => setEditing(null)}
          onSubmit={async values => {
            try {
              if (editing === 'new') {
                await create.mutateAsync(values);
                notifications.show({
                  message: 'User created — they need a password reset to log in',
                  color: 'green',
                });
              } else {
                await update.mutateAsync(values as AdminUser);
                notifications.show({ message: 'Saved', color: 'green' });
              }
              setEditing(null);
            } catch (err) {
              notifications.show({ message: errorMessage(err), color: 'red' });
            }
          }}
        />
      )}
    </Stack>
  );
}

interface UserEditModalProps {
  initial: Partial<AdminUser>;
  isNew: boolean;
  allAuthorities: string[];
  onClose(): void;
  onSubmit(values: Partial<AdminUser>): Promise<void>;
}

function UserEditModal({ initial, isNew, allAuthorities, onClose, onSubmit }: UserEditModalProps) {
  const form = useForm({
    initialValues: {
      id: initial.id,
      login: initial.login ?? '',
      firstName: initial.firstName ?? '',
      lastName: initial.lastName ?? '',
      email: initial.email ?? '',
      langKey: initial.langKey ?? 'en',
      activated: initial.activated ?? true,
      authorities: initial.authorities ?? ['ROLE_USER'],
    },
    validate: {
      login: v => (v.trim().length === 0 ? 'Login is required' : null),
      email: v => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Valid email is required'),
      authorities: v => (v.length === 0 ? 'At least one authority is required' : null),
    },
  });
  const [submitting, setSubmitting] = useState(false);

  return (
    <Modal opened onClose={onClose} title={isNew ? 'New user' : `Edit ${initial.login}`} size="lg" centered>
      <form
        onSubmit={form.onSubmit(async v => {
          setSubmitting(true);
          await onSubmit({
            id: v.id,
            login: v.login.trim(),
            firstName: v.firstName?.trim() || null,
            lastName: v.lastName?.trim() || null,
            email: v.email.trim(),
            langKey: v.langKey || null,
            activated: v.activated,
            authorities: v.authorities,
            // Preserve the existing image (e.g. a Google avatar); the form
            // doesn't edit it, and sending null wiped it on every save.
            imageUrl: initial.imageUrl ?? null,
          });
          setSubmitting(false);
        })}
      >
        <Stack gap="sm">
          <TextInput label="Login" required {...form.getInputProps('login')} />
          <Group grow>
            <TextInput label="First name" {...form.getInputProps('firstName')} />
            <TextInput label="Last name" {...form.getInputProps('lastName')} />
          </Group>
          <TextInput label="Email" required type="email" {...form.getInputProps('email')} />
          <TextInput label="Language" placeholder="en" {...form.getInputProps('langKey')} />
          <Switch
            label="Activated"
            description="Inactive accounts cannot sign in"
            checked={form.values.activated}
            onChange={e => form.setFieldValue('activated', e.currentTarget.checked)}
          />
          <MultiSelect
            label="Authorities"
            description="System roles. ROLE_ADMIN unlocks /users and /roles management."
            data={allAuthorities}
            value={form.values.authorities}
            onChange={v => form.setFieldValue('authorities', v)}
            searchable
            withAsterisk
            error={form.errors.authorities as string | undefined}
          />
          {isNew && (
            <Text size="xs" c="dimmed">
              A random password is set on create — the user gets a reset key. In production the legacy
              backend emails them a reset link.
            </Text>
          )}
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button type="submit" loading={submitting}>Save</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof HttpError) {
    if (typeof err.body === 'object' && err.body && 'detail' in err.body) {
      return String((err.body as { detail: unknown }).detail);
    }
    return `${err.status} on ${err.url}`;
  }
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}
